import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { ChevronLeft, Calendar, Users, MapPin, Clock, XCircle, Loader2, BadgeCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (imagePath) => {
  if (!imagePath) return 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400';
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/api/')) return `${BACKEND_URL}${imagePath}`;
  if (imagePath.startsWith('/uploads/')) return `${BACKEND_URL}/api${imagePath}`;
  return imagePath;
};

const statusColors = {
  confirmed: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  cancelled: 'bg-red-100 text-red-800',
  cancellation_requested: 'bg-orange-100 text-orange-800',
  completed: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
};

const cancelStatusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const getBookingStatus = (booking) => {
  if (booking.booking_status === 'cancellation_requested') return 'Cancellation Requested';
  if (booking.booking_status === 'cancelled') return 'Cancelled';
  if (booking.payment_status === 'paid' || booking.booking_status === 'confirmed') return 'Confirmed';
  if (booking.payment_status === 'pending') return 'Payment Pending';
  return booking.booking_status || 'Unknown';
};

const MyBookingsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchBookings();
    fetchCancellations();
  }, [isAuthenticated, user]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/bookings/user/${encodeURIComponent((user.email || '').trim())}`);
      if (response.data.success) setBookings(response.data.data || []);
    } catch { toast.error('Failed to load bookings'); }
    finally { setLoading(false); }
  };

  const fetchCancellations = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings/cancellations/user/${user.email}`);
      if (response.data.success) setCancellations(response.data.data || []);
    } catch (e) { console.error('Failed to load cancellations', e); }
  };

  const submitCancelRequest = async (bookingId) => {
    if (!cancelReason.trim()) { toast.error('Please provide a reason for cancellation'); return; }
    try {
      setSubmitting(true);
      const response = await axios.post(`${BACKEND_URL}/api/bookings/cancel-request`, {
        booking_id: bookingId, reason: cancelReason, guest_email: user.email
      });
      if (response.data.success) {
        toast.success('Cancellation request sent to host');
        setCancellingId(null);
        setCancelReason('');
        fetchBookings();
        fetchCancellations();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit cancellation');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-[#7CB342]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/')} className="mb-4">
            <ChevronLeft className="h-4 w-4 mr-2" /> Back to Home
          </Button>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold">My Bookings</h1>
            {user?.email_verified && (
              <span
                className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700"
                data-testid="email-verified-badge"
                title={user?.email_verified_at ? `Verified on ${new Date(user.email_verified_at).toLocaleDateString()}` : 'Email verified'}
              >
                <BadgeCheck className="h-3.5 w-3.5" />
                Email verified
              </span>
            )}
          </div>
          {user?.email && (
            <p className="text-sm text-gray-500 mt-1">{user.email}</p>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6" data-testid="bookings-tabs">
          <Button variant={activeTab === 'bookings' ? 'default' : 'outline'} onClick={() => setActiveTab('bookings')} className={activeTab === 'bookings' ? 'bg-[#7CB342] hover:bg-[#6da036]' : ''} data-testid="bookings-tab">
            Bookings ({bookings.length})
          </Button>
          <Button variant={activeTab === 'cancellations' ? 'default' : 'outline'} onClick={() => setActiveTab('cancellations')} className={activeTab === 'cancellations' ? 'bg-[#7CB342] hover:bg-[#6da036]' : ''} data-testid="cancellations-tab">
            Cancellations ({cancellations.length})
          </Button>
        </div>

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-4">
            {bookings.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-gray-500">No bookings yet</CardContent></Card>
            ) : bookings.map((booking, index) => (
              <Card key={booking.id || index} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`booking-card-${index}`}>
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    <Link to={`/property/${booking.property_id}`} className="md:w-48 h-40 md:h-auto flex-shrink-0 block">
                      <img src={getImageUrl(booking.property_image)} alt={booking.property_title} className="w-full h-full object-cover hover:opacity-90 transition-opacity" onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=400'; }} />
                    </Link>
                    <div className="flex-1 p-5">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <Link to={`/property/${booking.property_id}`} className="hover:text-[#7CB342] transition-colors">
                            <h3 className="font-semibold text-lg text-gray-900 hover:underline" data-testid="booking-property-link">{booking.property_title}</h3>
                          </Link>
                          <div className="flex items-center text-gray-500 text-sm mt-1">
                            <MapPin className="h-3 w-3 mr-1" />
                            <span>{booking.property_location || 'Location not available'}</span>
                          </div>
                        </div>
                        <Badge className={statusColors[booking.booking_status] || statusColors.pending} data-testid="booking-status-badge">
                          {getBookingStatus(booking)}
                        </Badge>
                      </div>
                      <Separator className="my-3" />
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="flex items-center text-gray-600"><Calendar className="h-3 w-3 mr-1.5" /><span>{booking.check_in}</span></div>
                        <div className="flex items-center text-gray-600"><Clock className="h-3 w-3 mr-1.5" /><span>{booking.check_out}</span></div>
                        <div className="flex items-center text-gray-600"><Users className="h-3 w-3 mr-1.5" /><span>{booking.guests} guests</span></div>
                        <div className="font-semibold text-gray-900">INR {booking.total_amount?.toLocaleString()}</div>
                      </div>

                      {/* Cancel button - only for confirmed bookings */}
                      {(booking.booking_status === 'confirmed' || booking.booking_status === 'paid') && (
                        <div className="mt-4">
                          {cancellingId === booking.id ? (
                            <div className="bg-red-50 rounded-lg p-4 space-y-3">
                              <p className="text-sm font-medium text-red-800">Reason for cancellation:</p>
                              <Textarea
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder="Please explain why you want to cancel..."
                                rows={3}
                                data-testid="cancel-reason-input"
                              />
                              <div className="flex gap-2">
                                <Button size="sm" variant="destructive" onClick={() => submitCancelRequest(booking.id)} disabled={submitting} data-testid="confirm-cancel-btn">
                                  {submitting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <XCircle className="h-3 w-3 mr-1" />}
                                  Submit Request
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => { setCancellingId(null); setCancelReason(''); }}>
                                  Back
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => setCancellingId(booking.id)} data-testid="cancel-booking-btn">
                              <XCircle className="h-3 w-3 mr-1" /> Request Cancellation
                            </Button>
                          )}
                        </div>
                      )}
                      {booking.booking_status === 'cancellation_requested' && (
                        <p className="text-sm text-orange-600 mt-3">Cancellation request sent — awaiting host response</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Cancellations Tab */}
        {activeTab === 'cancellations' && (
          <div className="space-y-4">
            {cancellations.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-gray-500">No cancellation requests</CardContent></Card>
            ) : cancellations.map((cancel, index) => (
              <Card key={cancel.cancellation_id || index} className="overflow-hidden" data-testid={`cancellation-card-${index}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{cancel.property_title}</h3>
                      <p className="text-sm text-gray-500">{cancel.check_in} to {cancel.check_out}</p>
                    </div>
                    <Badge className={cancelStatusColors[cancel.status]} data-testid="cancel-status-badge">
                      {cancel.status.charAt(0).toUpperCase() + cancel.status.slice(1)}
                    </Badge>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 mb-3">
                    <p className="text-sm"><strong>Your reason:</strong> {cancel.reason}</p>
                    <p className="text-sm text-gray-500 mt-1">Submitted: {new Date(cancel.created_at).toLocaleDateString()}</p>
                  </div>
                  {cancel.host_message && (
                    <div className={`rounded-lg p-3 ${cancel.status === 'accepted' ? 'bg-green-50' : 'bg-red-50'}`}>
                      <p className="text-sm"><strong>Host response:</strong> {cancel.host_message}</p>
                    </div>
                  )}
                  {cancel.status === 'accepted' && (
                    <p className="text-sm text-green-600 mt-2">Your booking has been cancelled and the dates are freed.</p>
                  )}
                  {cancel.status === 'rejected' && (
                    <p className="text-sm text-red-600 mt-2">Your cancellation was declined. Booking remains confirmed.</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBookingsPage;
